import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullSegment } from 'ts/shared/interfaces/MOResponse';
import { getStatus, reasons1, reasons2, reasons3, reasons4 } from 'constants/mo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import { sanitizeString, dateFormat } from 'utils/shared/utils';
import { createPortal } from 'react-dom';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoSpecialRequestModalProps {
  segment?: FullSegment;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit: (message: string, subject: string) => void;
  user?: any;
}

const MoSpecialRequestModal: FC<MoSpecialRequestModalProps> = ({
  segment,
  openModal,
  setOpenModal,
  user,
  ...props
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');

  useEffect(() => {
    if (openModal) {
      setMessage('');
    }
  }, [openModal]);

  const onClickCheckbox = (checked: boolean, reason: string) => {
    const reasonCleaned = reason.replace(/<[^>]*>?/g, '');

    if (checked && !message.includes(reasonCleaned)) {
      const needToAddRow = message ? '\n' : '';
      const newMessage = message + needToAddRow + reasonCleaned;
      setMessage(newMessage);
    } else if (!checked && message.includes(reasonCleaned)) {
      const needToRemoveRow = message.includes('\n' + reasonCleaned) ? '\n' : '';
      const newMessage = message.replace(needToRemoveRow + reasonCleaned, '');
      setMessage(newMessage);
    }
  };

  return createPortal(
    <div
      className="modal-overlay modal-overlay-top mo-message-modal-overlay modal-maximizable" style={{ direction: 'ltr' }}
      ref={modalOverlayRef}
    >
      <div ref={modalRef} className={classList + ' mo-message-modal'} style={modalStyle}>
        <div
          className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">
            {t('mo.support_ticket')}{' '}
            {segment && (
              <Fragment>
                {' '}-{' '}Order: {segment.orderId}{' '}
                Segment:
                {segment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(segment.roomsStatusCode)}`}>
                  {segment.roomsStatusCode}
                </span>
              </Fragment>
            )}
          </h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>

        <div className="modal-body text-xsm mo-message-modal-body">
          <div className="pb-10 mb-10 bb-1 text-sm flex-row" style={{ lineHeight: 2.3 }}>
            <span className="mr-10">
              <span className="mr-5">{t('mo.order_id')}:</span>
              <span className="mo-status mo-status-success">{segment?.orderId}</span>
            </span>
            <span className="mr-10">
              <span className="mr-5">{t('mo.segment_id')}:</span>
              <span className="mo-status mo-status-success">{segment?.orderSegId}</span>
            </span>
            <span className="mr-10">
              <span className="mr-5">{t('mo.check_in')}:</span>
              <span className="mo-status mo-status-success">
                {dateFormat(segment?.checkIn, 'dd-MMM-yy')}
              </span>
            </span>
            {!!segment?.suppPnr && (
              <span className="mr-10">
                <span className="mr-5">{t('mo.supplier_pnr')}:</span>
                <span className="mo-status mo-status-success">{segment?.suppPnr}</span>
              </span>
            )}
            <span className="mr-10">
              <span className="mr-5">{t('sort.name')}:</span>
              <span className="mo-status mo-status-success">
                {segment?.user_f_name} {segment?.user_l_name}
              </span>
            </span>
            <span className="mr-10">
              <span className="mr-5">{t('passenger.email')}:</span>
              <span className="mo-status mo-status-success">{user?.user_email}</span>
            </span>
          </div>
          <div>
            <FormGroup
              label={t('mo.subject')}
              value={subject}
              onChange={(val) => setSubject(val)}
            />
          </div>
          <div className="row checkbox-end ">
            <div className="mb-3 col-md-6 col-12 text-lh">
              <div className="title">{t('mo.room_preferences')}</div>
              {reasons1.map((reason, index) => (
                <Checkbox
                  className="mb-5"
                  key={index}
                  onChange={(val) => onClickCheckbox(val, t(reason.key))}
                  htmlLabel={<span dangerouslySetInnerHTML={{ __html: t(reason.key) }}></span>}
                />
              ))}
            </div>
            <div className="mb-3 col-md-6 col-12 text-lh">
              <div className="title">{t('mo.guest_notes')}</div>
              {reasons2.map((reason, index) => (
                <Checkbox
                  key={index}
                  className="mb-5"
                  onChange={(val) => onClickCheckbox(val, t(reason.key))}
                  htmlLabel={<span dangerouslySetInnerHTML={{ __html: t(reason.key) }}></span>}
                />
              ))}
              <div className="mb-5 text-bold color-danger">{t('mo.if_child')}</div>
              {reasons3.map((reason, index) => (
                <Checkbox
                  key={index}
                  className="mb-5"
                  onChange={(val) => onClickCheckbox(val, t(reason.key))}
                  htmlLabel={<span dangerouslySetInnerHTML={{ __html: t(reason.key) }}></span>}
                />
              ))}
              <div className="mb-5 text-bold color-danger">{t('mo.if_no_child')}</div>

              {reasons4.map((reason, index) => (
                <Checkbox
                  key={index}
                  className="mb-5"
                  onChange={(val) => onClickCheckbox(val, t(reason.key))}
                  htmlLabel={<span dangerouslySetInnerHTML={{ __html: t(reason.key) }}></span>}
                />
              ))}
            </div>
          </div>
          <div className="mb-5 mt-30 text-xsm">{t('mo.choose_requests_can_edit')}</div>
          <textarea
            className="form-control text-sm"
            onChange={(e) => setMessage(sanitizeString(e.target.value))}
            style={{ height: '150px', resize: 'none' }}
            value={message}
          />
        </div>

        <div className="modal-footer">
          <Button
            title={t('save')}
            className="btn btn-primary ml-0 mr-a"
            onClick={() => props.onSubmit(message, subject)}
          />
          <Button
            title={t('cancel')}
            className="btn btn-default"
            onClick={() => setOpenModal(false)}
          />
        </div>
      </div>
    </div>
    , document.body);
};

export default MoSpecialRequestModal;
